import { ITextStyles, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { Info } from 'components/Notification/Info';
import { downArrowIcon, globalFontBoldWeight, globalStackTokensGapExtraSmall, rightArrowIcon } from 'globalStyles';
import React, { useContext, useEffect, useState } from 'react';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { toBool } from 'utils/string';

interface IControlGroupProps {
  className?: string;
  defaultActive?: boolean;
  setActiveKey?: string;
  active?: boolean;
  title: string;
  disabled?: boolean;
  showBorder?: boolean;
  cacheKey?: LocalStorageKeys;
  children: React.ReactNode;
  disableFocus?: boolean;
  onToggle?: (expanded: boolean) => void;
  summary?: string; //show summary when group is collapsed
  info?: string; //show (i) info when group is expanded
}

const ControlGroup = (props: IControlGroupProps) => {
  const getDefaultState = (): boolean => {
    //1. When cacheKey is provided, try to load the state from local cache
    //2. If this fails or not provided, use defaultActive
    //3. If not provied, set to false
    let defState: boolean = false;

    if (props.cacheKey) {
      try {
        const cachedState = getLocalStorageData(appContext, props.cacheKey);
        defState = toBool(cachedState);
      } catch {
        if (props.defaultActive) defState = props.defaultActive;
      }
    } else if (props.defaultActive) {
      defState = props.defaultActive;
    }

    return defState;
  };

  const saveState = () => {
    if (props.cacheKey) {
      try {
        setLocalStorageData(appContext, props.cacheKey, activeState.toString());
      } catch {
        //ignore
      }
    }
  };

  const [activeState, setActiveState] = useState<boolean>(getDefaultState());
  const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const theme = getTheme();

  useEffect(() => {
    if (props.setActiveKey) {
      setActiveState(true);
    }
  }, [props.setActiveKey]);

  useEffect(() => {
    if (!firstTimeLoad) {
      saveState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeState]);

  useEffect(() => {
    if (props.disabled) {
      setActiveState(false);
    }
  }, [props.disabled]);

  useEffect(() => {
    if (props.active !== undefined) {
      if (props.defaultActive !== undefined && !firstTimeLoad) {
        setFirstTimeLoad(true);
      } else {
        setActiveState(props.active);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.active]);

  const getTextStylesTitle = (disabled: boolean): ITextStyles => {
    return {
      root: {
        fontWeight: globalFontBoldWeight,
        color: disabled
          ? appContext.useDarkMode
            ? darkTheme.palette?.neutralLighter
            : lightTheme.palette?.neutralQuaternary
          : undefined,
      },
    };
  };

  const getTextStylesSummary = (disabled: boolean): ITextStyles => {
    return {
      root: {
        color: disabled
          ? appContext.useDarkMode
            ? darkTheme.palette?.neutralLighter
            : lightTheme.palette?.neutralQuaternary
          : undefined,
        paddingRight: 10,
      },
    };
  };

  const toggleAccordion = () => {
    if (!props.disabled) {
      setActiveState(!activeState);
      if (props.onToggle && !firstTimeLoad) {
        props.onToggle(!activeState);
      }
    }
  };

  const getTitle = (): string => {
    return props.title;
  };

  const getSummary = (): string => {
    if (!props.summary) {
      return '';
    } else {
      return ' (' + props.summary + ')';
    }
  };

  //
  // Main render
  //
  return (
    <IPStack
      onFocusCapture={() => {
        if (!props.disableFocus && !activeState) {
          toggleAccordion();
        }
      }}
    >
      <IPStack.Item>
        <IPStack
          className={props.className}
          onClick={toggleAccordion}
          horizontal
          styles={{
            root: {
              cursor: 'pointer',
              height: 36,
              padding: 7,
            },
          }}
        >
          <IPStack
            horizontal
            styles={{ root: { overflow: 'hidden', paddingRight: 10 } }}
            tokens={globalStackTokensGapExtraSmall}
          >
            <IPStack.Item styles={{ root: { paddingTop: 2, paddingLeft: 4 } }}>
              {activeState ? <IPFontIcon {...downArrowIcon} /> : <IPFontIcon {...rightArrowIcon} />}
            </IPStack.Item>
            <IPStack.Item>
              <IPText nowrap styles={getTextStylesTitle(props.disabled ?? false)}>
                {getTitle()}
              </IPText>
            </IPStack.Item>
            {!activeState && (
              <IPStack.Item>
                <IPText block nowrap styles={getTextStylesSummary(props.disabled ?? false)}>
                  {getSummary()}
                </IPText>
              </IPStack.Item>
            )}
            {activeState && props.info && (
              <IPStack.Item>
                <Info message={props.info} />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item
        grow
        styles={{
          root: {
            borderRadius: props.showBorder ? '3px' : undefined,
            border: activeState && props.showBorder ? `1px solid ${theme.palette.neutralLight}` : undefined,
            maxHeight: activeState ? 2000 : 0,
            transition: activeState ? 'max-height 0.7s ease-in' : undefined,
            overflow: 'hidden',
            marginLeft: 26,
          },
        }}
      >
        {props.children}
      </IPStack.Item>
    </IPStack>
  );
};

export default ControlGroup;
